"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Role = exports.Amy = void 0;
const Course_1 = require("./Course");
const Language_1 = require("./Language");
const StudentAssignment_1 = require("./StudentAssignment");
/**
 * Amy.app works in tandem with Firebase.
 * Please make sure to load the firebase js library as well as the amy.app
 * library.
 *
 *
 * ## Example
 *
 * ```typescript
 * import firebase from "firebase";
 * import { initializeAmy, amyConfigs } from "@amy-app/amy-app-js-sdk";
 *
 * const firebaseApp = firebase.initializeApp(amyConfigs, "amy.app");
 * const amy = initializeAmy({ firebaseApp });
 * ```
 */
class Amy {
    /** @internal */
    constructor({ firebaseApp }) {
        this._user = null;
        this._roles = [];
        this.observersStarted = false;
        this._ready = false;
        this.readyObservers = new Map();
        this.terminators = [];
        this.fbApp = firebaseApp;
        // listen to user changes
        this.fbApp.auth().onAuthStateChanged((user) => {
            // user has first been initialized
            if (user && !this._user) {
                this._user = user;
                this.initObservers();
            }
            else if (user && this._user) {
                // nothing to do here. We already have a user
            }
            else if (!user && this._user) {
                // the user logged out.
                // clean up all observers and reset
                for (const terminator of this.terminators) {
                    terminator();
                }
                this._user = null;
                this._ready = false;
                this._roles = [];
                this.observersStarted = false;
            }
            this.informReadyObservers();
        });
    }
    initObservers() {
        if (!this._user) {
            return;
        }
        // prevent the observer to get stated mutliple times
        if (this.observersStarted) {
            return;
        }
        this.observersStarted = true;
        // listen to role changes
        const terminat = this.fbApp
            .firestore()
            .collection("roles")
            .where("userId", "==", this._user.uid)
            .onSnapshot((roleSnaps) => {
            this._roles = roleSnaps.docs.map((e) => new Role({
                partner: e.get("broker"),
                institutionId: e.get("schoolId"),
                classId: e.get("schoolClassId"),
                role: e.get("role"),
            }));
            this.informReadyObservers();
        });
        // add observer terminator
        this.terminators.push(terminat);
    }
    /**
     * After a user is logged in, it can take a while until everything is initialized.
     * You can use this observer to check if the amy is ready for the user
     * @param observer
     */
    readyObserver(observer) {
        observer(this._ready);
        const oId = `oID_${Math.random()}`;
        this.readyObservers.set(oId, observer);
        return () => {
            this.readyObservers.delete(oId);
        };
    }
    informReadyObservers() {
        this._ready = true;
        if (!this.user) {
            this._ready = false;
        }
        if (this._roles.length === 0) {
            this._ready = false;
        }
        for (const [_, observer] of this.readyObservers) {
            observer(this.ready);
        }
    }
    /**
     * Indicates if the user is ready and initialized
     */
    get ready() {
        return this._ready;
    }
    /**
     * The amy.app user that is logged in on this app
     */
    get user() {
        return this._user;
    }
    /** @internal */
    get firebaseApp() {
        return this.fbApp;
    }
    async signInViaToken(token) {
        const u = await this.fbApp.auth().signInWithCustomToken(token);
        this._user = u.user;
    }
    /**
     * Returns the all roles this user has.
     * In case the user is not logged in yet, it will return an empty array
     * @returns
     */
    get roles() {
        // if (!this._user) {
        //     throw new Error("User is not logged in");
        // }
        return this._roles;
    }
    /**
     * Returns an assignment ID which can be loaded via observer
     *
     * @param startArchetypeIds
     * @returns
     *
     *
     * ## example
     *
     * ```typescript
     * const assignmentId = await startAssignment(["cplx090010"]);
     * amy.studentAtudentAssignmentObserver(assignmentId, (assignment) => {console.log(assignment)})
     * ```
     */
    async startAssignment(startArchetypeIds = ["cplx090010"]) {
        if (!this._user) {
            throw new Error("User is not logged in");
        }
        const ref = this.fbApp.firestore().collection("StudentAssignments").doc();
        const a = {
            startArchetypeIds,
            studentId: this._user.uid,
            teachingType: "AMY",
            teachingSetupType: "ARRAY",
        };
        await ref.set(a);
        return ref.id;
    }
    /**
     * Starts an assignment tmeplate for the given role and returns an assignmentId.
     * The assignmentId can be use with the `studentAtudentAssignmentObserver(<assignmentId>, observer)`
     * to load an assignment.
     * @param options
     * @returns Returns a studentAssignmentId
     *
     * ## examples
     *
     * ```typescript
     * const studentAssignmentId = await startCourseAssignment(
     *  {
     *      role: amy.roles[0],
     *      courseAssignmentId: "couseAssignmentId123"
     *  });
     *
     * amy.studentAtudentAssignmentObserver(studentAssignmentId, (studentAssignment) => {
     *      console.log(studentAssignment);
     * })
     * ```
     */
    async startCourseAssignment(options) {
        if (!this.user) {
            throw new Error("User is not logged in");
        }
        return this.fbApp
            .functions("europe-west1")
            .httpsCallable("startAssignmentOnCall")({
            curriculumNodeId: options.courseAssignmentId,
            assignmentId: this.fbApp.firestore().collection("StudentAssignments").doc().id,
            studentId: this.user.uid,
            schoolClassId: options.role.courseId,
        })
            .then((result) => {
            return result.data.assignmentId;
        });
    }
    /**
     * The assignment observer is the most important function when working with amy.
     * It will update every time the assignment changes. You should us it to render
     * your webpage. The observer returns the assignemtn with all it's data.
     * This should allow you to write clean code, since you don't need any furhter
     * observers.
     *
     *
     * @param assignmentId The ID of an assignment. This ID is provided by amy.app
     * @param observer The observer function will be called for any changed in the assignment
     *
     * @return An unsubscribe function that can be called to cancel the assignment listener
     * @throws Will throw an error if the user is not authenticated
     *
     * ## example
     *
     * ```typescript
     * amy.studentAtudentAssignmentObserver(<assignmentId>, (assignment) => {
     *    // assuming you have a div with the id 'progress'.
     *    // You can simply write
     *    document.getElementById("progress").innerHTML = `${assignment.progress}`
     * })
     *
     * ```
     */
    studentAssignmentObserver(assignmentId, observer) {
        if (!this._user || this.roles.length === 0) {
            throw new Error("User not Authenticated");
        }
        let assigmentInitialized = false;
        let exerciseInitialized = false;
        let assignmentSnap;
        let bubbleSnaps;
        const assignmentTerminator = this.fbApp
            .firestore()
            .collection("StudentAssignments")
            .doc(assignmentId)
            .onSnapshot((_assignmentSnap) => {
            assigmentInitialized = true;
            assignmentSnap = _assignmentSnap;
            informObserver();
        });
        const exerciseTerminator = this.fbApp
            .firestore()
            .collection("ExerciseBubbles")
            .orderBy("createdAt", "asc")
            .where("studentAssignmentId", "==", assignmentId)
            .where("studentId", "==", this._user.uid)
            .onSnapshot((_bubbleSnaps) => {
            exerciseInitialized = true;
            bubbleSnaps = _bubbleSnaps;
            informObserver();
        });
        const self = this;
        function informObserver() {
            if (exerciseInitialized && assigmentInitialized && assignmentSnap && assignmentSnap.get("exercises")) {
                observer(new StudentAssignment_1.StudentAssignment({
                    id: assignmentId,
                    assignmentSnap,
                    bubbleSnaps,
                    parent: self,
                }));
            }
        }
        // add terminators to global terminator array
        this.terminators.push(assignmentTerminator);
        this.terminators.push(exerciseTerminator);
        return () => {
            assignmentTerminator();
            exerciseTerminator();
        };
    }
    courseObserver(role, observer) {
        this.getCourse({
            role,
        }).then((c) => {
            observer(c);
        });
        return () => {
            // TODO Terminate observation
        };
    }
    async getCourse(options) {
        const cousreSnap = await this.fbApp.firestore().collection("SchoolClasses").doc(options.role.courseId).get();
        if (!cousreSnap.exists) {
            throw new Error(`Course ${options.role.courseId} does not exist`);
        }
        const curriculumRef = cousreSnap.get("curriculumNodeId");
        const currSnap = await this.fbApp.database().ref(`curriculums`).get();
        const curriculum = currSnap.val();
        const currLangSnap = await this.fbApp.database().ref(`/i18n/en-NZ/curriculums`).get();
        const currLang = currLangSnap.val();
        // after all data is loaded we can populate the course
        const course = new Course_1.Course({
            amy: this,
            role: options.role,
            title: new Language_1.Language({ en: currLang[`${curriculumRef}_title`] || "" }),
            subtitle: new Language_1.Language(),
        });
        const findChildren = (rootRef) => {
            const root = curriculum[rootRef];
            if (root.nodeType === "section") {
                const ret = new Course_1.CourseSection({
                    title: new Language_1.Language({ en: currLang[`${root.id}_title`] || "" }),
                    subtitle: new Language_1.Language({ en: currLang[`${root.id}_subtitle`] || "" }),
                });
                ret.id = root.id;
                if (root && root.childIds) {
                    for (const childId of root.childIds) {
                        const child = findChildren(childId);
                        if (child) {
                            ret.sections.push(child);
                        }
                    }
                }
                return ret;
            }
            if (root.nodeType === "assignment") {
                const ret = new Course_1.CourseAssignment({
                    amy: this,
                    role: options.role,
                    title: new Language_1.Language({ en: currLang[`${root.id}_title`] || "" }),
                    subtitle: new Language_1.Language({ en: currLang[`${root.id}_subtitle`] || "" }),
                });
                ret.id = root.id;
                return ret;
            }
            return null;
        };
        const course2 = findChildren(curriculumRef);
        course.sections.push(...course2.sections);
        // const root = currSnap.val()[curriculumRef];
        return course;
    }
}
exports.Amy = Amy;
class Role {
    /** @internal */
    constructor(options) {
        this.partnerId = options.partner;
        this.role = options.role;
        this.courseId = options.classId;
        this.institutionId = options.institutionId;
    }
}
exports.Role = Role;
//# sourceMappingURL=Amy.js.map